/**
 * StaffEditHeader — back link, identity block, workshop counts, and the
 * edit/save/cancel controls for the staff detail page.
 *
 * Split out of StaffEditPage (BON-398); behaviour unchanged.
 */
import { ArrowLeft, Loader2, Pencil, Save as SaveIcon, X as XIcon } from 'lucide-react';

import type { StaffMemberDetail } from '../../api/staff';

export interface StaffEditHeaderProps {
  member: StaffMemberDetail;
  isEditing: boolean;
  saving: boolean;
  onBack: () => void;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}

function EditControls({
  saving,
  onCancel,
  onSave,
}: Pick<StaffEditHeaderProps, 'saving' | 'onCancel' | 'onSave'>) {
  return (
    <div className="ml-2 flex items-center gap-2">
      <button
        onClick={onCancel}
        disabled={saving}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800 disabled:opacity-50"
      >
        <XIcon size={14} /> Cancel
      </button>
      <button
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-moss-600 text-white hover:bg-moss-700 disabled:opacity-50"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <SaveIcon size={14} />}
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

export default function StaffEditHeader(props: StaffEditHeaderProps) {
  const { member, isEditing, saving, onBack, onStartEdit, onCancel, onSave } = props;

  return (
    <div className="mb-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 mb-4 transition"
      >
        <ArrowLeft size={14} /> Back to Staff
      </button>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-moss-100 dark:bg-moss-900/30 text-moss-700 dark:text-moss-400 flex items-center justify-center text-lg font-bold flex-shrink-0">
          {member.firstName?.[0]?.toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-bark-900 dark:text-stone-100 truncate">
            {member.name}
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 truncate">
            {member.email || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-2 text-sm text-stone-500 dark:text-stone-400">
            <span className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-xs">
              {member.workshopCount} workshop{member.workshopCount !== 1 ? 's' : ''}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400 text-xs">
              {member.upcomingWorkshops} upcoming
            </span>
          </div>
          {isEditing ? (
            <EditControls saving={saving} onCancel={onCancel} onSave={onSave} />
          ) : (
            <button
              onClick={onStartEdit}
              className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
